import React, { useEffect, useState } from 'react';

import { useToast } from '@chakra-ui/react';

import { deleteOrders } from 'api/order';
import { useMutation, useQueryClient } from 'react-query';

import { washHub } from 'store/slices';
import { useWashHubListener } from 'hooks';
import { useDebouncedValue } from 'hooks/useDebouncedValue';

import { addDays, subDays } from 'date-fns';

import { OrdersProps } from './Orders.props';
import OrdersView from './Orders.view';
import useQueryParams from 'hooks/useQueryParams';
import useUrlSharing from 'hooks/useUrlSharing';

const OrdersContainer = (_: OrdersProps) => {
  const toast = useToast();
  const queryClient = useQueryClient();
  const queryUrl = useQueryParams();

  const [endDate, setEndDate] = useState<Date>();
  const [startDate, setStartDate] = useState<Date>();
  const [status, setStatus] = useState<string>();
  const [invoiced, setInvoiced] = useState<boolean | undefined>();
  const [venue, setVenue] = useState<string[]>();
  const [accountManagers, setAccountManagers] = useState<string[]>();
  const [feeAdjustmentTypes, setFeeAdjustmentTypes] = useState<string[]>();
  const [defaultIndex, setDefaultIndex] = useState<number>(0);
  // Seeded from the URL so a shared link searches on first load, without
  // waiting out the debounce.
  const [searchInput, setSearchInput] = useState(queryUrl.search ?? '');
  const [urlRecord, setUrlRecord] = useState({});
  const [isUrlMapped, setIsUrlMapped] = useState<boolean>(false);

  const washHubId = washHub.selectors.useSelectedWashHubId();
  const search = useDebouncedValue(searchInput.trim());

  const isUpdated = useUrlSharing(urlRecord, washHubId);

  const { mutate, isLoading: isDeleting } = useMutation(deleteOrders);

  useEffect(() => {
    const urlParams = {
      washHubId: String(washHubId),
      ...(status ? { status } : {}),
      ...(defaultIndex ? { defaultIndex: `${defaultIndex}` } : {}),
      ...(startDate ? { startDate: `${startDate.valueOf()}` } : {}),
      ...(endDate ? { endDate: `${endDate.valueOf()}` } : {}),
      ...(venue ? { venue: venue.join('-') } : {}),
      ...(accountManagers
        ? { accountManagers: accountManagers.join('-') }
        : {}),
      ...(typeof invoiced === 'boolean' ? { invoiced: invoiced } : {}),
      ...(feeAdjustmentTypes
        ? { feeAdjustmentTypes: feeAdjustmentTypes.join('-') }
        : {}),
      ...(search ? { search } : {}),
    };
    setUrlRecord(urlParams);
  }, [
    washHubId,
    endDate,
    startDate,
    status,
    venue,
    accountManagers,
    defaultIndex,
    invoiced,
    feeAdjustmentTypes,
    search,
  ]);

  useEffect(() => {
    const {
      status,
      venue,
      accountManagers,
      defaultIndex,
      startDate,
      endDate,
      invoiced,
      feeAdjustmentTypes,
    } = queryUrl;
    status && setStatus(status);
    venue && setVenue(venue.split('-'));
    accountManagers && setAccountManagers(accountManagers.split('-'));
    feeAdjustmentTypes && setFeeAdjustmentTypes(feeAdjustmentTypes.split('-'));
    typeof invoiced === 'boolean' && setInvoiced(invoiced);
    defaultIndex && setDefaultIndex(+defaultIndex || 0);
    startDate && setStartDate(new Date(+startDate));
    endDate && setEndDate(new Date(+endDate));
    if (isUpdated) setIsUrlMapped(true);
  }, [isUpdated]);

  useWashHubListener(() => {
    setStartDate(undefined);
    setEndDate(undefined);
    setStatus(undefined);

    onClearFilters();
  });

  const onSearchPress = (startDate: Date | null, endDate: Date | null) => {
    if (startDate) {
      setStartDate(startDate);
    }

    if (endDate) {
      setEndDate(endDate);
    }

    setStatus(undefined);
  };

  const onSearchClear = () => {
    setStartDate(undefined);
    setEndDate(undefined);
    setStatus(undefined);
  };

  const onStatusClick = (_status?: string) => {
    setStatus(_status);

    const today = new Date();

    if (_status === 'overdue') {
      const yesterday = subDays(today, 1);

      setEndDate(yesterday);
      setStartDate(undefined);
    } else if (_status === 'today') {
      setEndDate(today);
      setStartDate(today);
    } else if (_status === 'tomorrow') {
      const tomorrow = addDays(today, 1);

      setEndDate(tomorrow);
      setStartDate(tomorrow);
    } else if (_status === 'future') {
      const tomorrow = addDays(today, 1);

      setEndDate(undefined);
      setStartDate(tomorrow);
    } else {
      setStartDate(undefined);
      setEndDate(undefined);
    }
  };

  const onDelete = (ids: string[], _onSuccess: () => void) =>
    mutate(ids, {
      onSuccess: () => {
        toast({
          status: 'success',
          title: 'Success',
          description: 'Orders Deleted',
          isClosable: true,
          duration: 3000,
        });

        queryClient.invalidateQueries(`${washHubId}-fetch-orders`);

        _onSuccess();
      },
    });

  const onApplyFilters = (data: any, type: string) => {
    if (type === 'invoice') {
      setInvoiced(data);
    } else if (type === 'accountManagers') {
      setAccountManagers(data);
    } else if (type === 'feeAdjustmentTypes') {
      setFeeAdjustmentTypes(data);
    } else {
      setVenue(data);
    }
  };

  const onClearFilters = () => {
    setVenue(undefined);
    setAccountManagers(undefined);
    setInvoiced(undefined);
    setFeeAdjustmentTypes(undefined);
  };

  const _props = {
    status,
    venue,
    accountManagers,
    feeAdjustmentTypes,
    startDate,
    endDate,
    isDeleting,
    defaultIndex,
    invoiced,
    setDefaultIndex,
    onDelete,
    onSearchPress,
    onSearchClear,
    onStatusClick,
    onApplyFilters,
    onClearFilters,
    searchInput,
    search,
    onSearchChange: setSearchInput,
  };

  return <>{isUrlMapped && <OrdersView {..._props} />}</>;
};

export default OrdersContainer;
